import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation, Trans } from 'react-i18next';
import {
  ArrowRight,
  BarChart3,
  Check,
  MapPinOff,
  MessageSquare,
  QrCode,
  Server,
  ShieldCheck,
  Sparkles,
  Timer,
  UserRound,
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { partnerAPI } from '../services/api';
import LoadingIndicator from '../components/LoadingIndicator';

/**
 * /business — the B2B storefront (plan_b2b_self_serve.md §2–3).
 *
 * Story arc (design/copy council, Aug 1): hero with the poster image →
 * business case → "live today" proof band → three steps → audience →
 * in-the-field imagery + real dashboard → pricing → FAQ → trust → close.
 * Photography (the Scan-to-Play poster set, shared with the homepage) does
 * the explaining; social proof is product truth, never invented logos.
 *
 * i18n: EN primary, DE secondary — copy lives in
 * src/i18n/locales/{en,de}/business.json.
 */

// Billed by venue size, never by session meters — caps are invisible abuse
// ceilings, not counters venues should fear (plan §3 decision). The actual
// billing lives in Stripe (STRIPE_PRICE_IDS: plan_partner_*).
const PLANS = [
  { name: 'Starter', price: '249', audienceKey: 'pricing.planStarterAudience', featuresKey: 'pricing.starterFeatures', featured: false },
  { name: 'Standard', price: '490', audienceKey: 'pricing.planStandardAudience', featuresKey: 'pricing.standardFeatures', featured: true },
  { name: 'Pro', price: '990', audienceKey: 'pricing.planProAudience', featuresKey: 'pricing.proFeatures', featured: false },
];

const STEP_KEYS = [
  { icon: MessageSquare, title: 'steps.step1Title', text: 'steps.step1Text' },
  { icon: Sparkles, title: 'steps.step2Title', text: 'steps.step2Text' },
  { icon: QrCode, title: 'steps.step3Title', text: 'steps.step3Text' },
];

// The "why you pay" section — outcome arguments, deliberately qualitative
// (no invented ROI numbers). Order per council review: service gap →
// experience → cost → bonus; insight is the "and also" closer, not a lead.
// Each ROI card carries a one-word "lever" kicker so the 2x2 scans at a glance.
const VALUE_KEYS = [
  { kicker: 'value.card1Kicker', title: 'value.card1Title', text: 'value.card1Text' },
  { kicker: 'value.card2Kicker', title: 'value.card2Title', text: 'value.card2Text' },
  { kicker: 'value.card4Kicker', title: 'value.card4Title', text: 'value.card4Text' },
  { kicker: 'value.card3Kicker', title: 'value.card3Title', text: 'value.card3Text' },
];

// Compliance grid — the questions IT/legal/board actually ask, one card each.
// All statements are current product truth (see /privacy and /terms).
const TRUST_CARDS = [
  { icon: UserRound, title: 'trust.card1Title', text: 'trust.card1Text' },
  { icon: Timer, title: 'trust.card2Title', text: 'trust.card2Text' },
  { icon: MapPinOff, title: 'trust.card3Title', text: 'trust.card3Text' },
  { icon: BarChart3, title: 'trust.card4Title', text: 'trust.card4Text' },
  { icon: Server, title: 'trust.card5Title', text: 'trust.card5Text' },
  { icon: ShieldCheck, title: 'trust.card6Title', text: 'trust.card6Text' },
];

// The capability spectrum in verbs — what the guide does in the field.
// All six are live product truth (no "coming soon" rows, on purpose) —
// "Speak" is the voice layer shipped Aug 2026: full narration + voice input.
// Custom claymorphic 3D icon set (generated, Airbnb-category-icon style).
const CAPABILITIES = [
  { img: '/home/cap-narrate.webp', title: 'capabilities.narrateTitle', text: 'capabilities.narrateText' },
  { img: '/home/cap-speak.webp', title: 'capabilities.speakTitle', text: 'capabilities.speakText' },
  { img: '/home/cap-navigate.webp', title: 'capabilities.navigateTitle', text: 'capabilities.navigateText' },
  { img: '/home/cap-play.webp', title: 'capabilities.playTitle', text: 'capabilities.playText' },
  { img: '/home/cap-answer.webp', title: 'capabilities.answerTitle', text: 'capabilities.answerText' },
  { img: '/home/cap-together.webp', title: 'capabilities.togetherTitle', text: 'capabilities.togetherText' },
];

// Use cases as poster-in-scene photo cards; the last one deliberately breaks
// out of tourism to keep the aperture general.
const AUDIENCE_BLOCKS = [
  { img: '/home/scan2play-town.webp', title: 'audience.block1Title', text: 'audience.block1Text' },
  { img: '/home/scan2play-museum.webp', title: 'audience.block2Title', text: 'audience.block2Text' },
  { img: '/home/scan2play-zoo.webp', title: 'audience.block3Title', text: 'audience.block3Text' },
  { img: '/home/scan2play-factory.webp', title: 'audience.block4Title', text: 'audience.block4Text' },
];

const FAQ_KEYS = [1, 2, 3, 4, 5];

// Shared CTA styles (same vocabulary as the homepage B2B section)
const CTA_PRIMARY =
  'inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-emerald-400 hover:bg-emerald-300 text-[#0a0a0a] font-semibold rounded-2xl transition-colors';
const CTA_SECONDARY =
  'inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-white/20 hover:border-white/40 text-white font-semibold rounded-2xl transition-colors';

// Airbnb-inspired section header: big friendly centered type, one short
// muted line underneath, whitespace instead of rules doing the separation.
const SectionHeader = ({ kicker, title, subtitle, className = '' }) => (
  <div className={`text-center max-w-2xl mx-auto mb-14 ${className}`}>
    {kicker && (
      <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-4">
        {kicker}
      </p>
    )}
    <h2 className="text-3xl sm:text-[2.75rem] sm:leading-[1.15] font-bold tracking-tight">
      {title}
    </h2>
    {subtitle && <p className="text-white/45 text-base mt-4 leading-relaxed">{subtitle}</p>}
  </div>
);

const BusinessPage = () => {
  const { t } = useTranslation('business');
  const { user } = useAuth();
  const isRealUser = Boolean(user) && user?.authProvider !== 'guest';

  // Marketing page stays visitable when logged in, but the chrome should
  // acknowledge it: venue owners get a dashboard door instead of the funnel.
  // Same query key as Layout — shared cache, no extra request.
  const { data: myVenueResponse } = useQuery({
    queryKey: ['partner-mine'],
    queryFn: () => partnerAPI.getMine(),
    enabled: isRealUser,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const myVenue = myVenueResponse?.data?.data?.partner || null;

  // Owners are past onboarding: every "Get started" becomes "Open dashboard"
  const ctaTarget = myVenue ? `/p/${myVenue.slug}/dashboard` : '/business/start';
  const ctaLabel = myVenue ? t('hero.ctaDashboard') : t('hero.ctaStart');

  useEffect(() => {
    document.title = 'Okio for Business — a personal AI guide for every visitor';
    return () => {
      document.title = 'Okio';
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      {/* Sticky so Pricing and the CTA stay reachable on a long sales page;
          the pill slot belongs to the primary action, not the pricing anchor. */}
      <header className="sticky top-0 z-40 bg-gray-950/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-6 md:px-8 h-14 flex items-center justify-between">
          <Link to="/"><img src="/logo_white.webp" alt="OKIO" className="h-6 md:h-7 w-auto" /></Link>
          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="#pricing"
              className="hidden sm:inline text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              {t('header.pricing')}
            </a>
            <a
              href="mailto:hello@okio.ai"
              className="hidden sm:inline text-sm font-medium text-white/60 hover:text-white transition-colors"
            >
              {t('header.contact')}
            </a>
            <LanguageSwitcher />
            <Link
              to={ctaTarget}
              className="text-sm font-semibold text-[#0a0a0a] bg-emerald-400 hover:bg-emerald-300 rounded-full px-4 py-2 transition-colors whitespace-nowrap"
            >
              {myVenue ? t('header.openDashboard') : t('header.getStarted')}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero — big centered type, then one wide image card doing the showing */}
      <section className="max-w-6xl mx-auto px-6 pt-16 lg:pt-24 pb-14 lg:pb-20">
        {/* The app's thinking-eyes, repurposed as the hero mascot. The flex
            wrapper shrink-wraps the component (its own max-w-xs would
            otherwise left-align it) so the scale stays centered; the `dark`
            scope turns the eye rings white on this always-dark page. */}
        <div className="dark flex justify-center mb-8 lg:mb-10">
          <div className="scale-[1.8] md:scale-[2.4] transform-gpu">
            <LoadingIndicator className="!mt-0 !py-0 !px-0 !max-w-none" />
          </div>
        </div>
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-emerald-400 text-xs font-semibold uppercase tracking-widest mb-6">
            {t('hero.kicker')}
          </p>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.06] mb-6 max-w-2xl mx-auto">
            {t('hero.titleLine1')}
          </h1>
          <p className="text-white/50 text-lg sm:text-xl leading-relaxed max-w-lg mx-auto mb-10">
            {t('hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 mb-5">
            <Link to={ctaTarget} className={CTA_PRIMARY}>
              {ctaLabel}
              <ArrowRight size={16} />
            </Link>
            <Link to="/starters" className={CTA_SECONDARY}>
              {t('hero.ctaDemo')}
            </Link>
          </div>
          <a
            href={`mailto:hello@okio.ai?subject=${encodeURIComponent(t('pricing.destinationMailSubject'))}`}
            className="text-white/40 hover:text-white/70 text-sm transition-colors"
          >
            {t('hero.ctaContact')} →
          </a>
        </div>

      </section>

      {/* Moment strip — the first visual: visitors mid-experience across
          verticals, so every kind of buyer sees their place within one or two
          swipes. Breaks out of the text column into a wider bleed. */}
      <section className="max-w-[105rem] mx-auto pb-4">
        <div className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 px-6">
          {[
            ['/home/scan2play-trail.webp', 4],
            ['/home/moment-oldtown.webp', 3],
            ['/home/moment-zoo.webp', 1],
            ['/home/moment-museum.webp', 2],
            ['/home/moment-garden.webp', 5],
            ['/home/moment-castle.webp', 6],
            ['/home/moment-factory.webp', 7]
          ].map(([src, n]) => (
            <div
              key={n}
              className="relative flex-shrink-0 w-[76%] sm:w-[45%] md:w-[360px] lg:w-[400px] snap-center aspect-[4/5] rounded-3xl overflow-hidden"
            >
              <img
                src={src}
                alt={t(`hero.moment${n}Kicker`)}
                loading={n === 1 || n === 2 ? 'eager' : 'lazy'}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                <p className="text-emerald-400 text-[11px] font-bold tracking-wider uppercase mb-1.5">
                  {t(`hero.moment${n}Kicker`)}
                </p>
                <p className="text-white text-base sm:text-lg font-semibold leading-snug">
                  {t(`hero.moment${n}Caption`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Capability spectrum — the product definition, Airbnb category-list
          style: custom 3D icon left, title + text right, 2-col editorial grid.
          mix-blend-lighten dissolves each render's baked near-black background
          into the page so the clay objects float freely. */}
      <section className="max-w-5xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('capabilities.title')} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-y-14 gap-x-16">
          {CAPABILITIES.map((cap) => (
            <div key={cap.title} className="flex items-center gap-6">
              <img
                src={cap.img}
                alt=""
                loading="lazy"
                className="flex-shrink-0 w-24 h-24 md:w-28 md:h-28 mix-blend-lighten"
              />
              <div>
                <h3 className="text-white font-semibold text-lg mb-1.5">{t(cap.title)}</h3>
                <p className="text-white/45 text-[15px] leading-relaxed max-w-sm">{t(cap.text)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Differentiators — why this is categorically not an audio guide or a
          chatbot. Alternating rows, clay dioramas (same custom 3D language as
          the capability icons, scaled up) with mix-blend-lighten. */}
      <section className="max-w-5xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader
          kicker={t('diff.kicker')}
          title={t('diff.title')}
          subtitle={t('diff.subtitle')}
        />
        <div className="space-y-8 lg:space-y-10">
          {[
            ['/home/diff-smart.webp', 1],
            ['/home/diff-route.webp', 2],
            ['/home/diff-group.webp', 3],
            ['/home/diff-build.webp', 4],
          ].map(([src, n], index) => (
            <div
              key={n}
              className={`flex flex-col md:items-center gap-6 md:gap-14 ${
                index % 2 ? 'md:flex-row-reverse' : 'md:flex-row'
              }`}
            >
              <img
                src={src}
                alt=""
                loading="lazy"
                className="w-56 sm:w-64 md:w-2/5 max-w-xs mx-auto md:mx-0 mix-blend-lighten"
              />
              <div className="md:flex-1 text-center md:text-left">
                <p className="text-emerald-400 text-[11px] font-bold tracking-widest uppercase mb-3">
                  {t(`diff.row${n}Kicker`)}
                </p>
                <h3 className="text-white font-semibold text-2xl mb-3">
                  {t(`diff.row${n}Title`)}
                </h3>
                <p className="text-white/50 text-[15px] leading-relaxed max-w-md mx-auto md:mx-0">
                  {t(`diff.row${n}Text`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why it pays off — the ROI argument. Soft cards with a one-word lever
          kicker: shaped like a business case, deliberately image-free between
          the icon list above and the photo cards below. */}
      <section className="max-w-5xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('value.title')} subtitle={t('value.subtitle')} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {VALUE_KEYS.map((card) => (
            <div
              key={card.title}
              className="rounded-[2rem] bg-white/[0.04] p-8 sm:p-10"
            >
              <p className="text-emerald-400 text-[11px] font-bold tracking-widest uppercase mb-4">
                {t(card.kicker)}
              </p>
              <h3 className="text-white font-semibold text-xl mb-3">{t(card.title)}</h3>
              <p className="text-white/50 text-[15px] leading-relaxed">{t(card.text)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Use cases — "Made for places people explore" carries the photo-card
          design (poster-in-scene per audience), then the real dashboard as the
          operator's side of the same story. Absorbs the old "In the field". */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('audience.title')} subtitle={t('audience.subtitle')} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {AUDIENCE_BLOCKS.map((block) => (
            <div key={block.title} className="rounded-[2rem] overflow-hidden bg-gray-900 border border-white/10">
              <div className="relative aspect-[3/2]">
                <img
                  src={block.img}
                  alt={`Scan to Play poster — ${t(block.title)}`}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover object-[50%_35%]"
                />
              </div>
              <div className="px-6 py-5">
                <p className="text-emerald-400 text-[11px] font-bold tracking-wider uppercase mb-1.5">
                  {t(block.title)}
                </p>
                <p className="text-white/70 text-[15px] leading-relaxed">{t(block.text)}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-sm mt-6 text-center">
          <Trans
            t={t}
            i18nKey="audience.note"
            components={[
              <a
                href="mailto:hello@okio.ai"
                className="text-emerald-400 hover:underline"
              >
                .
              </a>,
            ]}
          />
        </p>
      </section>

      {/* The operator's side — real product UI as its own beat: the guide is
          out front, the venue sits at the controls. The screenshot is the
          social proof we actually have. */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('dashboard.title')} subtitle={t('dashboard.subtitle')} />
        <div className="rounded-[2rem] overflow-hidden border border-white/20 bg-black mb-12">
          <div className="flex items-center gap-1.5 px-5 py-3.5 border-b border-white/20">
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/15" />
            <span className="ml-3 text-white/30 text-xs">{t('dashboard.kicker')}</span>
          </div>
          <img
            src="/home/dashboard-peek.webp"
            alt="OKIO venue dashboard with live statistics"
            loading="lazy"
            className="w-full"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8">
          {[1, 2, 3].map((n) => (
            <div key={n}>
              <h3 className="text-white font-semibold text-base mb-2">
                {t(`dashboard.point${n}Title`)}
              </h3>
              <p className="text-white/45 text-sm leading-relaxed">
                {t(`dashboard.point${n}Text`)}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Go-live — one beat, right before pricing: it's real (statement) →
          here's how (three steps) → go touch it (mood image + CTA). Absorbs
          the old separate proof band and steps sections. */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-5">
            {t('proofBand.title')}
          </h2>
          <p className="text-white/50 text-base sm:text-lg leading-relaxed">
            {t('proofBand.subtitle')}
          </p>
        </div>

        {/* Breather, and evidence for the headline directly above it. No
            caption: this close to the H2 it would only restate it. */}
        <div className="relative rounded-[2rem] overflow-hidden aspect-[3/2] sm:aspect-[16/9] lg:aspect-[21/9] mb-14 lg:mb-20">
          <img
            src="/home/live-today-mood.webp"
            alt="Two visitors following a live guide above an old town at sunset"
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 mb-14 lg:mb-20">
          {STEP_KEYS.map((step, index) => (
            <div key={step.title} className="text-center md:px-2">
              <p className="text-6xl font-bold text-emerald-400/25 leading-none mb-6">
                {index + 1}
              </p>
              <h3 className="text-white font-semibold text-xl mb-2.5">{t(step.title)}</h3>
              <p className="text-white/45 text-[15px] leading-relaxed">{t(step.text)}</p>
            </div>
          ))}
        </div>

        {/* Concierge setup — what the one-time fee in the AGB actually buys.
            Sits with the three steps, not in Pricing, because it explains the
            build rather than the running cost. */}
        <div className="rounded-[2rem] border border-emerald-400/30 bg-gradient-to-br from-emerald-500/10 to-transparent p-8 lg:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <p className="text-emerald-300 text-[10px] font-bold uppercase tracking-widest mb-3">
                {t('setup.kicker')}
              </p>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4">
                {t('setup.title')}
              </h3>
              <p className="text-white/50 leading-relaxed mb-6">{t('setup.text')}</p>
              <p className="mb-1">
                <span className="text-4xl font-bold">{t('setup.price')}</span>
              </p>
              <p className="text-white/40 text-sm">{t('setup.priceNote')}</p>
            </div>
            <div className="lg:pt-8">
              <ul className="space-y-3 text-sm text-white/60 mb-6">
                {t('setup.items', { returnObjects: true }).map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <Check size={15} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-white/30 text-xs border-t border-white/10 pt-4">
                {t('setup.planNote')}
              </p>
            </div>
          </div>
        </div>

      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('pricing.title')} subtitle={t('pricing.subtitle')} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 items-stretch">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={`rounded-[2rem] p-8 border flex flex-col ${
                plan.featured
                  ? 'bg-emerald-500/10 border-emerald-400/40 ring-1 ring-emerald-400/40 md:-my-3 md:py-10'
                  : 'bg-gray-900 border-white/10'
              }`}
            >
              {plan.featured && (
                <p className="text-emerald-300 text-[10px] font-bold uppercase tracking-widest mb-2">
                  {t('pricing.featured')}
                </p>
              )}
              <h3 className="text-white font-semibold text-lg mb-1">{plan.name}</h3>
              <p className="text-white/40 text-xs mb-5">{t(plan.audienceKey)}</p>
              <p className="mb-6">
                <span className="text-4xl font-bold">€{plan.price}</span>
                <span className="text-white/40 text-sm">{t('pricing.perMonth')}</span>
              </p>
              <ul className="space-y-2.5 text-sm text-white/60 mb-7 flex-1">
                {t(plan.featuresKey, { returnObjects: true }).map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check size={15} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Link
                to={ctaTarget}
                className={`block text-center px-6 py-2.5 rounded-2xl text-sm font-semibold transition-colors ${
                  plan.featured
                    ? 'bg-emerald-400 hover:bg-emerald-300 text-[#0a0a0a]'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                {t('pricing.choosePlan', { plan: plan.name })}
              </Link>
            </div>
          ))}
        </div>
        <p className="text-white/40 text-xs text-center mb-6">{t('pricing.microcopy')}</p>
        <p className="text-white/30 text-xs text-center max-w-2xl mx-auto">
          <Trans
            t={t}
            i18nKey="pricing.destinationNote"
            components={[
              <a
                href={`mailto:hello@okio.ai?subject=${encodeURIComponent(t('pricing.destinationMailSubject'))}`}
                className="text-emerald-400 hover:underline"
              >
                .
              </a>,
            ]}
          />
        </p>
      </section>

      {/* FAQ — the objection ledger */}
      <section className="max-w-3xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('faq.title')} />
        <div>
          {FAQ_KEYS.map((n) => (
            <div key={n} className="border-t border-white/10 py-6">
              <h3 className="text-white font-semibold text-base mb-2">{t(`faq.q${n}`)}</h3>
              <p className="text-white/50 text-[15px] leading-relaxed">{t(`faq.a${n}`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Trust — compliance grid: the questions IT, legal and the board ask,
          one card each, scannable (single-card bullet list retired) */}
      <section className="max-w-6xl mx-auto px-6 py-20 lg:py-28">
        <SectionHeader title={t('trust.title')} subtitle={t('trust.subtitle')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
          {TRUST_CARDS.map((card) => (
            <div key={card.title} className="rounded-[2rem] bg-white/[0.04] p-7">
              <span className="inline-flex w-11 h-11 rounded-2xl bg-emerald-500/15 items-center justify-center mb-5">
                <card.icon size={19} className="text-emerald-400" />
              </span>
              <h3 className="text-white font-semibold text-base mb-2">{t(card.title)}</h3>
              <p className="text-white/45 text-sm leading-relaxed">{t(card.text)}</p>
            </div>
          ))}
        </div>
        <p className="text-white/30 text-xs text-center">
          {t('trust.details')}{' '}
          <Link to="/privacy" className="text-emerald-400 hover:underline">
            {t('trust.privacy')}
          </Link>
          {' · '}
          <Link to="/terms" className="text-emerald-400 hover:underline">
            {t('trust.terms')}
          </Link>
        </p>
      </section>

      {/* Closing CTA — on a photographic bed, not floating on gray */}
      <section className="relative w-full mt-10">
        <div className="relative py-28 sm:py-36 overflow-hidden">
          <img
            src="/home/scan2play-oldtown.webp"
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-[50%_30%]"
          />
          <div className="absolute inset-0 bg-black/75" />
          <div className="relative max-w-2xl mx-auto px-6 text-center">
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight mb-5">
              {t('closing.title')}
            </h2>
            <p className="text-white/60 text-base mb-10">{t('closing.subtitle')}</p>
            <Link to={ctaTarget} className={`${CTA_PRIMARY} px-10 py-4`}>
              {ctaLabel}
              <ArrowRight size={16} />
            </Link>
            {/* The early-adopter truth, kept at the moment of decision */}
            <p className="text-white/40 text-sm mt-8">{t('closing.founders')}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-10 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <img src="/logo_white.webp" alt="OKIO" className="h-5 w-auto opacity-50" />
          <div className="flex items-center gap-5 text-xs text-white/40">
            <Link to="/imprint" className="hover:text-white transition-colors">
              {t('footer.imprint')}
            </Link>
            <Link to="/privacy" className="hover:text-white transition-colors">
              {t('footer.privacy')}
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              {t('footer.terms')}
            </Link>
            <a href="mailto:hello@okio.ai" className="hover:text-white transition-colors">
              hello@okio.ai
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BusinessPage;
